import { useCallback, useContext, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getFeatureTitle } from "../../../../esri/custom-popup-content";
import { ConfigContext } from "../../../../utils/ConfigContext";

import { RightArrowIcon } from "../../../Icons";
import { StyledEditorBreadcrumbs } from "../Editor-styled";
import { EditorContext, UpdateType } from "../EditorContextProvider";
import { ALL_STEPS, EditType } from "../EditorSwiper/EditorSwiper";
import { getFeatureNameField } from "../helpers";
import FeatureHeader from "./FeatureHeader";
import LayerHeader from "./LayerHeader/LayerHeader";

const EditorHeader = ({
  slides,
  popupFeature,
  handlePrev,
  editType,
  activeIndex,
  goBack,
  title,
  showIcon = true,
}) => {
  const {
    activeColor,
    updateType,
    editableLayer,
    setShowWarning,
    highlightSymbol,
    highlightFeature,
    batchUpdateFeatures,
    addedPoints,
    hasEditedAttachments,
  } = useContext(EditorContext);

  const { config } = useContext(ConfigContext);

  const {
    formState: { isDirty },
    reset,
  } = useFormContext();

  const nameField = useMemo(
    () => getFeatureNameField(editableLayer),
    [editableLayer]
  );

  const value = useWatch({ name: nameField }) || null;

  const { t } = useTranslation("common");

  useEffect(() => {
    const currSlide = slides[activeIndex];
    if (
      !(
        (editableLayer &&
          currSlide?.id === ALL_STEPS.fillFields &&
          (isDirty ||
            (Array.isArray(addedPoints) && addedPoints.length > 0) ||
            hasEditedAttachments)) ||
        EditType.create === editType
      )
    )
      return;

    const handleBeforeUnload = (event) => {
      return;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [
    editableLayer,
    slides,
    activeIndex,
    isDirty,
    editType,
    addedPoints,
    hasEditedAttachments,
  ]);

  const processForm = useCallback(
    (callback, action, condition = false) => {
      try {
        const currSlide = slides[activeIndex];
        if (
          (editableLayer &&
            currSlide?.id === ALL_STEPS.fillFields &&
            (isDirty ||
              (Array.isArray(addedPoints) && addedPoints.length > 0) ||
              hasEditedAttachments)) ||
          condition
        ) {
          setShowWarning({
            show: true,
            action: action,
          });
        } else {
          // resetFields();
          callback();
        }
      } catch (error) {
        // resetFields();
        callback();
      }
    },
    [slides, activeIndex, editableLayer, isDirty, editType, addedPoints]
  );

  const featureTitle = useMemo(() => {
    let title;
    if (highlightFeature) {
      title = getFeatureTitle(highlightFeature, t);
    }

    if (value != null && typeof value === "string") {
      title = value;
    }

    return title;
  }, [highlightFeature, t, editableLayer, value]);

  const showBack = useMemo(() => {
    try {
      if (Array.isArray(slides) && slides.length + 1 < activeIndex) return null;
      const slide = slides[activeIndex];
      if (slide.id === "entryPoints" && updateType === UpdateType.situational) {
        return false;
      } else {
        return true;
      }
    } catch (err) {
      return true;
    }
  }, [slides, activeIndex]);

  const showArrow = !popupFeature && editableLayer && showBack;

  return (
    <StyledEditorBreadcrumbs
      style={{
        marginBottom: 0,
        minHeight: 54,
      }}
      hasProgress
    >
      {showArrow && (
        <button
          style={{
            zIndex: 1,
          }}
          onClick={() => {
            processForm(handlePrev, "back");
          }}
        >
          <RightArrowIcon width={18} color="var(--fontColor)" />
        </button>
      )}

      <div
        style={{
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          width: "100%",
          gap: "8px",
          padding: showArrow ? "0px 10px" : "0px 10px 0px 0px",
          overflow: "hidden",
        }}
      >
        {!featureTitle ? (
          <LayerHeader
            editableLayer={editableLayer}
            editType={editType}
            updateType={updateType}
            t={t}
            config={config}
            activeColor={activeColor}
            batchUpdateFeatures={batchUpdateFeatures}
            defaultTitle={title}
            showIcon={showIcon}
          />
        ) : (
          <FeatureHeader
            highlightSymbol={highlightSymbol}
            title={featureTitle}
          />
        )}
      </div>
      <button
        style={{ marginRight: 2 }}
        onClick={() => {
          processForm(goBack, "close", EditType.create === editType);
        }}
      >
        <svg
          fillRule="evenodd"
          role="img"
          viewBox="0 0 10 10"
          aria-label="description"
          fill="var(--fontColor)"
        >
          <path d="M6.32 5L10 8.68 8.68 10 5 6.32 1.32 10 0 8.68 3.68 5 0 1.32 1.32 0 5 3.68 8.68 0 10 1.32 6.32 5z"></path>
        </svg>
      </button>
    </StyledEditorBreadcrumbs>
  );
};

export default EditorHeader;
